import { MultiMetricOptionMixin } from '../../mixin';
import metricTranslatorMixin from '@/mixins/translator/metric';

import QuickControlDropdown from '../Dropdown/index';
import { useFieldGuard } from '../useFieldGuard';

import * as S from '../styles';

import { IFRAME_EVENT_MESSAGE } from '@consts';
import { MAX_QUICK_CONTROL_METRIC_COUNT } from '../../consts';
import { makeMetricFormat } from '../../utils';

export default {
    name: 'MetricQuickControl',
    mixins: [MultiMetricOptionMixin, metricTranslatorMixin],
    setup(props, { emit }) {
        // Vue 2에서는 믹스인의 setup이 병합되지 않으므로 직접 호출
        const mixinSetup = MultiMetricOptionMixin.setup(props);
        useFieldGuard({
            getSelectedId: () => props.selectedField,
            getEditableIds: () => props.exposureList,
            getFixedIds: () => props.fixedList,
            getDefaultIds: () => props.defaultList,
            emitUpdateList: (newList) =>
                emit('update:exposureListMap', {
                    key: props.exposureListKey,
                    value: newList,
                }),
            emitUpdateSelected: (newSelected) =>
                emit('update:exposureSelectedFieldMap', newSelected),
        });

        return mixinSetup;
    },
    props: {
        panelKey: String,
        optionTitle: String,
        hide: { type: Boolean, default: false },
        /**
         * 선택된 metric key (string)
         */
        selectedField: { type: String, default: '' },
        /**
         * exposureListMap 에서 사용할 key
         */
        exposureListKey: { type: String, required: true },
        /**
         * 선택된 metric key 목록 (string[] | undefined)
         * undefined = 미설정 상태 (default list 사용 중)
         */
        exposureList: { type: Array, default: undefined },
        /**
         * exposureList가 undefined일 때 표시할 기본 목록 (string[])
         */
        defaultList: { type: Array, default: () => [] },
        /**
         * 드롭다운에 항상 고정 표시할 옵션 목록 (string[])
         * e.g. [''] for None, ['all'] for no-grouping
         */
        fixedList: { type: Array, default: () => [] },
        canEditCustomMetric: { type: Boolean, default: true },
    },
    data() {
        return {
            iframeListeners_: [
                {
                    eventType: IFRAME_EVENT_MESSAGE.UPDATE_METRIC_PANEL,
                    listener: (e) => {
                        const { data } = e;
                        if (!data || data.panelKey !== this.panelKey) return;

                        const { selectedFields } = data;

                        this.$emit('update:exposureListMap', {
                            key: this.exposureListKey,
                            value: selectedFields,
                        });
                    },
                },
            ],
        };
    },
    computed: {
        title() {
            return this.$t(
                'FIELD_PANEL.metric_exposure_setting.second_panel_title',
                { option: this.optionTitle }
            );
        },
        mainGuide() {
            return this.$t(
                'FIELD_PANEL.metric_exposure_setting.second_panel_main_guide'
            );
        },
        subGuide() {
            return this.$t(
                'FIELD_PANEL.metric_exposure_setting.second_panel_sub_guide'
            );
        },
        /**
         * Override mixin: openReactPanel/syncSelectedFields 에서 사용
         * undefined(미설정) 상태에서는 defaultList를 현재 선택 목록으로 간주
         */
        selectedFields() {
            return this.exposureList ?? this.defaultList;
        },
        limitCount() {
            return MAX_QUICK_CONTROL_METRIC_COUNT;
        },
    },
    methods: {
        handleListUpdate(newList) {
            // newList = string[] (ExposureDropdown reorder 결과)
            this.$emit('update:exposureListMap', {
                key: this.exposureListKey,
                value: newList,
            });
        },
        handleRemoveItem(key) {
            const updated = (this.exposureList ?? this.defaultList).filter((v) => v !== key);

            this.$emit('update:exposureListMap', {
                key: this.exposureListKey,
                value: updated,
            });

            if (this.selectedField === key) {
                this.$emit('update:exposureSelectedFieldMap', updated[0] ?? this.fixedList[0] ?? '');
            }
        },
        handleSelectItem(key) {
            this.$emit('update:exposureSelectedFieldMap', key);
        },
        renderItem() {
            const {
                exposureList,
                defaultList,
                fixedList,
                selectedField,
                getMetricName,
            } = this;

            return (
                <S.ExposureDropdownWrapper>
                    <QuickControlDropdown
                        fieldType="metric"
                        exposureList={exposureList ?? defaultList}
                        selectedValue={selectedField}
                        translator={(key) => key === '' ? this.$t('NEW_DASHBOARD.none') : getMetricName(makeMetricFormat(key))}
                        fixedList={fixedList}
                        on={{
                            'update:list': this.handleListUpdate,
                            'remove:item': this.handleRemoveItem,
                            'select:item': this.handleSelectItem,
                        }}
                    />
                </S.ExposureDropdownWrapper>
            );
        },
    },
    render() {
        const { optionTitle, wrapOptions, renderItem } = this;

        return (
            <S.ComponentWrapper hide={this.hide}>
                {wrapOptions({
                    headerContent: renderItem(),
                    title: optionTitle,
                })}
            </S.ComponentWrapper>
        );
    },
};
